package mapsearch.buildings

import apiclient.FormationClient
import apiclient.geoobjects.ObjectTags
import apiclient.geoobjects.ObjectType
import apiclient.geoobjects.SearchQueryContext
import apiclient.geoobjects.newContext
import apiclient.geoobjects.restSearch
import apiclient.tags.buildingId
import apiclient.tags.defaultFloorId
import apiclient.tags.floorId
import apiclient.tags.floorLevel
import apiclient.tags.tag
import auth.WorkspacesStore
import dev.fritz2.core.RootStore
import dev.fritz2.core.storeOf
import koin.koinCtx
import koin.withKoin
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.combine
import mainmenu.AppStateStore
import model.AppPhase
import model.Building
import model.Floor

class BuildingLookUpStore : RootStore<Map<String, Building>>(emptyMap(), Job()) {

    val workspacesStore: WorkspacesStore by koinCtx.inject()
    val appStateStore: AppStateStore by koinCtx.inject()

    fun reset() {
        update(emptyMap())
    }

    var fetching = storeOf(false, job)

    private suspend fun fetchAllBuildings(workspaceIds: List<String>? = null) {
        withKoin {
            val buildingTilesLookup: BuildingTilesLookup = get()

            if (!fetching.current) {
                try {
                    fetching.update(true)
                    withKoin {
                        val client = get<FormationClient>()
                        (workspaceIds ?: workspacesStore.current.map { it.groupId }).let { groupIds ->
                            client.restSearch(
                                SearchQueryContext.newContext(groupIds).copy(
                                    objectTypes = listOf(
                                        ObjectType.Building,
                                        ObjectType.Floor,
                                        ObjectType.Unit,
                                    ),
                                    excludeTags = listOfNotNull(
                                        ObjectTags.Deleted.tag(true),
                                        ObjectTags.Archived.tag(true),
                                    ),
                                    size = 1000,
                                ),
                            ).fold(
                                { results ->
                                    val buildingResults = results.hits.filter {
                                        it.hit.objectType == ObjectType.Building
                                    }
                                    val floors = results.hits.filter {
                                        it.hit.objectType == ObjectType.Floor //&& it.hit.tags.buildingId != null
                                    }.groupBy {
                                        it.hit.tags.buildingId
                                    }
                                    val units = results.hits.filter {
                                        it.hit.objectType == ObjectType.Unit && it.hit.tags.floorId != null
                                    }.groupBy {
                                        it.hit.tags.floorId
                                    }

                                    buildingResults.map { it.hit }.map { building ->
                                        val buildingFloors = floors[building.id].orEmpty().map { floor ->
                                            val floorUnits = units[floor.hit.id].orEmpty().map { unit ->
                                                unit.hit
                                            }
                                            Floor(floor.hit, floorUnits)
                                        }.groupBy {
                                            it.floor.tags.floorLevel ?: 0.0
                                        }
                                        val defaultFloorLevel =
                                            buildingFloors.toMutableMap().getFloorLevel(building.tags.defaultFloorId)
                                        Building(
                                            buildingId = building.id,
                                            buildingName = building.title,
                                            defaultFloorLevel = defaultFloorLevel,
                                            defaultFloorId = building.tags.defaultFloorId,
                                            activeFloorLevel = defaultFloorLevel,
                                            activeFloorIds = buildingFloors[defaultFloorLevel].orEmpty()
                                                .map { it.floor.id },
                                            floorData = buildingFloors.toMutableMap(),
                                            geoObjectDetails = building,
                                        )
                                    }.associateBy { it.buildingId }.let { buildings ->
                                        buildingTilesLookup.add(buildings)
                                        update(buildings)
                                    }
                                },
                                { error ->
                                    update(emptyMap())
                                    console.error("Failed to fetch buildings.", error.message, error.cause)
                                },
                            )
                        }
                    }
                } finally {
                    fetching.update(false)
                }
            }
        }
    }

    val updateBuildings = handle<Map<String, Building>> { current, newBuildings ->
        withKoin {
            val buildingTilesLookup: BuildingTilesLookup = get()

            val mutable = current.toMutableMap()
            newBuildings.entries.forEach { (buildingId, building) ->
                mutable[buildingId] = building
                buildingTilesLookup.updateBuildingInTileLookUp(building)
            }
            mutable
        }
    }

    init {
        combine(appStateStore.data, workspacesStore.data) { a, w -> Pair(a.appPhase, w) } handledBy { (appPhase, workspaces) ->
            if (appPhase == AppPhase.LoggedInAnonymous || appPhase == AppPhase.LoggedIn) {
                workspaces.mapNotNull { it.groupId.ifBlank { null } }
                    .ifEmpty { null }?.let { workspaceIds ->
                        fetchAllBuildings(workspaceIds)
                    }
            }
        }
    }
}

